import React, { useState } from 'react';
import {
  CheckCircle2,
  PhoneCall,
  Clock,
  Headset,
  X
} from 'lucide-react';
import { functionsAsia } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { Link } from "wouter";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";

export default function RequestCallback() {
  // Form State
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [reason, setReason] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const openForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setBusinessName("");
    setReason("");
    setIsSubmitting(false);
    setIsSuccess(false);
    setError("");
    setShowCallbackForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const sendCallback = httpsCallable(functionsAsia, 'triggerCallbackEmail');
      await sendCallback({
        name,
        email,
        phone,
        businessName,
        reason
      });

      setIsSuccess(true);

      setTimeout(() => {
        setShowCallbackForm(false);
      }, 3000);
    } catch {
      setError("Something went wrong. Please try again or email support directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">

      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link href="/">
            <div className="cursor-pointer hover:opacity-80 transition-opacity active:scale-95">
              <Logo size="md" />
            </div>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <ThemeToggle />
            <Button
              variant="default"
              size="sm"
              className="gap-1.5"
              onClick={openForm}
            >
              <PhoneCall className="h-4 w-4" />
              <span className="hidden sm:inline">Request Call</span>
              <span className="sm:hidden">Call Me</span>
            </Button>
          </div>
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <main className="flex-1 pb-20">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto bg-card shadow-xl rounded-b-3xl md:rounded-b-[3rem] overflow-hidden border-x border-b border-border">
          {/* MOBILE FIX: Replaced rigid aspect ratio with flexible padding */}
          <div className="relative w-full py-16 md:py-24 bg-slate-900 flex flex-col items-center justify-center text-center px-6 overflow-hidden">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-red-500 via-slate-900 to-slate-900"></div>

            <Headset className="h-14 w-14 md:h-16 md:w-16 text-red-400 mb-4 relative z-10" />
            <h1 className="text-3xl md:text-5xl font-black text-white relative z-10 tracking-tight leading-tight">
              Need Personalized Help?
            </h1>
            <p className="text-slate-300 mt-4 max-w-lg relative z-10 text-sm md:text-base leading-relaxed">
              Whether you need help setting up your store, understanding Pro features, or troubleshooting hardware, our experts are ready to talk.
            </p>
          </div>

          <div className="p-6 md:p-12 text-center">
            <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-medium">
              Schedule a free, one-on-one consultation with the DailyHisaab support team. We're here to ensure your business runs flawlessly.
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="max-w-4xl mx-auto px-4 mt-8 space-y-8">
          {/* MOBILE FIX: Ensured cards stack nicely with good padding */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-card rounded-3xl p-6 md:p-8 shadow-sm border border-border text-center flex flex-col items-center">
              <div className="h-14 w-14 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <Clock className="h-7 w-7 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-foreground">24-Hour Response</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">We aim to call you back within one business day to resolve your queries quickly.</p>
            </div>

            <div className="bg-card rounded-3xl p-6 md:p-8 shadow-sm border border-border text-center flex flex-col items-center">
              <div className="h-14 w-14 bg-amber-50 dark:bg-amber-900/20 rounded-full flex items-center justify-center mb-4">
                <Headset className="h-7 w-7 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-foreground">Expert Guidance</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Speak directly with product specialists who understand your retail business needs.</p>
            </div>

            <div className="bg-card rounded-3xl p-6 md:p-8 shadow-sm border border-border text-center flex flex-col items-center sm:col-span-2 md:col-span-1">
              <div className="h-14 w-14 bg-emerald-50 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-foreground">100% Free</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Support and onboarding consultations are completely free for all our users.</p>
            </div>
          </div>

          {/* Call to Action Button */}
          <div className="flex justify-center mt-4">
            <button
              onClick={openForm}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-4 px-8 rounded-2xl shadow-lg shadow-primary/20 transition-transform active:scale-95 text-lg"
            >
              <PhoneCall className="h-5 w-5" />
              <span>Request a Call Back</span>
            </button>
          </div>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border py-8 bg-background mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Logo size="sm" />
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with ❤️ in Bengal by{" "}
                <a href="https://asldigitalsolutions.in" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">&copy;{new Date().getFullYear()} DailyHisaab. All Rights Reserved</p>
            </div>
          </div>
        </div>
      </footer>

      {/* ── POPUP FORM ── */}
      {showCallbackForm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-background rounded-[2rem] shadow-2xl border border-border overflow-hidden flex flex-col max-h-[92vh] md:max-h-[85vh]">

            {/* MOBILE FIX: Larger touch target for the close button */}
            <button
              onClick={() => setShowCallbackForm(false)}
              className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center rounded-full text-primary-foreground/70 hover:bg-black/20 hover:text-white transition-colors z-10 active:scale-90"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="bg-primary px-6 pt-8 pb-6 shrink-0">
              <h2 className="text-2xl font-bold text-primary-foreground flex items-center gap-2 tracking-tight">
                <PhoneCall className="h-6 w-6" /> We'll call you
              </h2>
              <p className="text-sm text-primary-foreground/90 mt-2 font-medium">
                Drop your details below and our team will reach out.
              </p>
            </div>

            <div className="overflow-y-auto flex-1 custom-scrollbar">
              {isSuccess ? (
                <div className="px-6 py-16 flex flex-col items-center text-center gap-4">
                  <div className="h-20 w-20 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-2">
                    <CheckCircle2 className="h-10 w-10 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold text-foreground tracking-tight">Request Received!</p>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    Thank you, <strong className="text-foreground">{name}</strong>. Our support team has been notified and will call you at <strong className="text-foreground">{phone}</strong> within 24 hours.
                  </p>
                  <button
                    onClick={() => setShowCallbackForm(false)}
                    className="mt-4 w-full py-3.5 rounded-xl border-2 border-border font-bold text-foreground active:scale-95 transition-transform"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-5">
                  {/* MOBILE FIX: text-base to prevent iOS zoom, h-12 for better touch areas */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-foreground">Your Name *</label>
                    <input type="text" required placeholder="Enter your full name" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-border bg-background text-foreground text-base outline-none focus:ring-2 focus:ring-primary/40 transition-shadow" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-foreground">Phone Number *</label>
                    <input type="tel" required placeholder="10-digit mobile number" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-border bg-background text-foreground text-base outline-none focus:ring-2 focus:ring-primary/40 transition-shadow" maxLength={15} />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-foreground">Email Address *</label>
                    <input type="email" required placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-border bg-background text-foreground text-base outline-none focus:ring-2 focus:ring-primary/40 transition-shadow" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-foreground">Business Name <span className="text-muted-foreground font-normal">(Optional)</span></label>
                    <input type="text" placeholder="Your shop or business name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-border bg-background text-foreground text-base outline-none focus:ring-2 focus:ring-primary/40 transition-shadow" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-bold text-foreground">How can we help? <span className="text-muted-foreground font-normal">(Optional)</span></label>
                    <textarea placeholder="Tell us briefly what you'd like to discuss..." value={reason} onChange={(e) => setReason(e.target.value)} className="w-full min-h-[100px] p-4 rounded-xl border border-border bg-background text-foreground text-base outline-none focus:ring-2 focus:ring-primary/40 resize-none transition-shadow" />
                  </div>

                  {error && <p className="text-sm text-destructive font-bold p-3 bg-destructive/10 rounded-lg">{error}</p>}

                  {/* MOBILE FIX: Extra padding bottom to ensure submit button is easily clickable above mobile keyboards */}
                  <div className="pt-2 pb-4">
                    <button type="submit" disabled={isSubmitting || !name.trim() || !email.trim() || !phone.trim()} className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold text-base transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-md">
                      {isSubmitting ? "Submitting..." : "Submit Request"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}